import { supabase } from '@/lib/supabase';
import { env } from '@/lib/env';
import { isStandalone } from '@/features/install/platform';

/**
 * רישום ל Web Push (מסמך התכנון 4.5).
 *
 * באייפון Push עובד רק כשהאפליקציה מותקנת למסך הבית, ולכן שם לא
 * מבקשים הרשאה לפני ההתקנה: בקשה שנדחית פעם אחת קשה להחזיר.
 */
export type PushState =
  | 'unsupported'
  | 'needs-install'
  | 'not-configured'
  | 'default'
  | 'granted'
  | 'denied';

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

export function pushState(): PushState {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (isIOS() && !isStandalone()) return 'needs-install';
  if (!env.vapidPublicKey) return 'not-configured';
  return Notification.permission as 'default' | 'granted' | 'denied';
}

/**
 * המפתח הציבורי מגיע כ base64url, ו pushManager.subscribe דורש
 * Uint8Array מעל ArrayBuffer ממש, ולא מעל ArrayBufferLike.
 */
function toUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);

  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return view;
}

export type SubscribeResult =
  | { ok: true }
  | { ok: false; reason: PushState | 'failed'; message: string };

export async function subscribeToPush(employeeId: string): Promise<SubscribeResult> {
  const state = pushState();

  if (state === 'unsupported') {
    return { ok: false, reason: state, message: 'הדפדפן הזה לא תומך בהתראות.' };
  }
  if (state === 'needs-install') {
    return {
      ok: false,
      reason: state,
      message: 'באייפון התראות עובדות רק אחרי שמתקינים את האפליקציה למסך הבית.',
    };
  }
  if (state === 'not-configured') {
    return { ok: false, reason: state, message: 'התראות לא מוגדרות עדיין במערכת.' };
  }
  if (state === 'denied') {
    return {
      ok: false,
      reason: state,
      message: 'ההתראות חסומות בהגדרות הדפדפן. אפשר להפעיל אותן משם.',
    };
  }

  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();

  if (permission !== 'granted') {
    return { ok: false, reason: 'denied', message: 'ההרשאה לא ניתנה.' };
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: toUint8Array(env.vapidPublicKey),
      }));

    const json = subscription.toJSON();
    if (!json.keys?.p256dh || !json.keys?.auth || !json.endpoint) {
      return { ok: false, reason: 'failed', message: 'הרישום להתראות נכשל. נסו שוב.' };
    }

    // endpoint ייחודי, ולכן מכשיר שנרשם שוב מעדכן ולא מכפיל
    const { error } = await supabase.from('push_subscriptions').upsert(
      {
        employee_id: employeeId,
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      },
      { onConflict: 'endpoint' },
    );
    if (error) throw error;

    return { ok: true };
  } catch {
    return { ok: false, reason: 'failed', message: 'הרישום להתראות נכשל. נסו שוב.' };
  }
}

/** ניתוק המכשיר הנוכחי, בלי לגעת במכשירים אחרים של אותו עובד. */
export async function unsubscribeFromPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  await supabase.from('push_subscriptions').delete().eq('endpoint', subscription.endpoint);
  await subscription.unsubscribe();
}
